import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { IconBadge } from '../../atoms/iconBadge/IconBadge'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { ConversationTag } from '../../ui-models/messages/Conversations'
import { tID } from '../../utils/utils'

export type ConversationItemTagProps = {
  tags: ConversationTag[]
}

export const ConversationItemTag: React.FC<ConversationItemTagProps> = ({ tags = [] }) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const primaryTag = tags[0]

  const badgeData = useMemo(
    () => ({
      [ConversationTag.INACTIVE]: {
        gradient: undefined,
        backgroundColor: colors.backgroundInactive,
        variant: BadgeVariant.NEUTRAL,
        textColor: colors.textInactive,
        text: intl.formatMessage({
          defaultMessage: 'INACTIVE',
          description: 'Badge for an inactive client',
        }),
      },
      [ConversationTag.UNSCHEDULED]: {
        gradient: undefined,
        backgroundColor: colors.backgroundWarning,
        variant: BadgeVariant.ATTENTION,
        textColor: colors.textWarning,
        text: intl.formatMessage({
          defaultMessage: 'UNSCHEDULED',
          description: 'Badge for an unscheduled client',
        }),
      },
      [ConversationTag.AI_COACH_ENABLED]: {
        gradient: 'ai' as const,
        variant: BadgeVariant.LYRA_AI,
        textColor: colors.textInverse,
        text: intl.formatMessage({
          defaultMessage: 'LYRA AI',
          description: 'Badge for a client with AI Coach enabled',
        }),
      },
      [ConversationTag.AT_RISK]: {
        gradient: undefined,
        variant: BadgeVariant.ALERT,
        textColor: colors.textInverse,
        text: intl.formatMessage({
          defaultMessage: 'RISK',
          description: 'Badge indicating the client is at risk',
        }),
      },
      [ConversationTag.SELF_HARM]: {
        gradient: undefined,
        variant: BadgeVariant.ALERT,
        textColor: colors.textInverse,
        text: intl.formatMessage({
          defaultMessage: 'SELF HARM',
          description: 'Badge indicating the client has self harm',
        }),
      },
    }),
    [
      colors.backgroundInactive,
      colors.backgroundWarning,
      colors.textInactive,
      colors.textInverse,
      colors.textWarning,
      intl,
    ],
  )

  const selectedBadge = badgeData[primaryTag]

  const text = useMemo(() => {
    if (selectedBadge?.text) {
      return tags.length > 1 ? `${selectedBadge.text} + ${tags.length - 1}` : selectedBadge.text
    }
    return ''
  }, [tags.length, selectedBadge?.text])

  const ToolTipContent = (
    <View>
      {tags.map((tag) => {
        const isLast = tag === tags[tags.length - 1]
        return (
          <BodyText
            text={badgeData[tag]?.text}
            size={BodyTextSize.SMALL}
            key={tag}
            style={{ color: colors.textInverse }}
          >
            {
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              !isLast ? ' • ' : ''
            }
          </BodyText>
        )
      })}
    </View>
  )

  const Badge = (
    <IconBadge
      testID={tID('ConversationItemTag')}
      text={text}
      variant={selectedBadge?.variant ?? BadgeVariant.NEUTRAL}
      shape={BadgeShape.RECT}
      gradient={selectedBadge?.gradient}
    />
  )

  if (tags.length > 1) {
    return (
      <Tooltip
        testID={tID('ConversationItemTooltip')}
        content={ToolTipContent}
        placement='top'
        hoverEnabled
        wrapperStyle={{ flexGrow: 0 }}
      >
        {Badge}
      </Tooltip>
    )
  } else {
    return Badge
  }
}
